import React from 'react';
import { NSButton, NSCard } from 'bricks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './ProFormaExitValuationCard.scss';
import { numeralFormatter } from 'ns_libs/formatter';
import { faChartMixedUpCircleDollar, faMagnifyingGlassDollar } from '@fortawesome/pro-solid-svg-icons';
import { useProFormaSocketContext } from '../ProForma/socketContext/ProFormaSocketProvider';
import { KEY_STATS_LABELS } from '../../../../constants/proForma';

interface ProFormaExitValuationCardProps {
    exitValuation: number;
    leveredIRR: number;
    leveredEMx: number;
    stabilizedYieldOnCost: number;
    equityProceeds: number;
    id?: string;
    status?: 'best' | 'good' | 'danger' | 'warning';
    onCreateProForma: () => void;
}

const ProFormaExitValuationCard: React.FC<ProFormaExitValuationCardProps> = ({
    exitValuation, leveredIRR, leveredEMx, stabilizedYieldOnCost, equityProceeds, id, status = 'success', onCreateProForma,
}) => {
    const { showCents } = useProFormaSocketContext();
    const formatCurrency = (value: number) => new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: showCents ? 2 : 0,
        maximumFractionDigits: showCents ? 2 : 0,
    }).format(value);

    const cardData = [
        {
            id: 'levered-irr',
            title: KEY_STATS_LABELS.LEVERED_IRR,
            value: numeralFormatter(leveredIRR, '0,0.00%'),
            icon: faMagnifyingGlassDollar,
            iconClass: 'ProFormaExitValuation__floating-dollar',
        },
        {
            id: 'levered-emx',
            title: KEY_STATS_LABELS.LEVERED_EMX,
            value: `${numeralFormatter(leveredEMx, '0,0.00')}x`,
            icon: faChartMixedUpCircleDollar,
            iconClass: 'ProFormaExitValuation__floating-chart',
        },
        {
            id: 'stab-yield-on-cost',
            title: KEY_STATS_LABELS.STAB_YIELD_ON_COST,
            value: numeralFormatter(stabilizedYieldOnCost, '0,0.00%'),
            icon: faMagnifyingGlassDollar,
            iconClass: 'ProFormaExitValuation__floating-dollar',
        },
        {
            id: 'net-sale-proceeds',
            title: KEY_STATS_LABELS.EQUITY_PROCEEDS,
            value: formatCurrency(equityProceeds),
            icon: faChartMixedUpCircleDollar,
            iconClass: 'ProFormaExitValuation__floating-chart',
        },
    ];

    return (
        <div className={`ProFormaExitValuation ProFormaExitValuation--${status}`}>
            <NSCard className="NSCard--level-4 p-2">
                <div className="ProFormaExitValuation__valuation-container ProFormaExitValuation__valuation-container--primary">
                    <div className="ProFormaExitValuation__card-title">{KEY_STATS_LABELS.EXIT_VALUATION.toUpperCase()}</div>
                    <div className="ProFormaExitValuation__card-valuation text-white">{formatCurrency(exitValuation)}</div>
                </div>

                <div className="ProFormaExitValuation__cards-grid">
                    {cardData.map(card => (
                        <div key={card.id} className="ProFormaExitValuation__valuation-container">
                            <FontAwesomeIcon icon={card.icon} className={card.iconClass} />
                            <div className="ProFormaExitValuation__card-title">{card.title}</div>
                            <div className="ProFormaExitValuation__card-subtitle text-white">{card.value}</div>
                        </div>
                    ))}
                </div>

                <div className="ProFormaExitValuation__separator" />
                <NSButton
                    id="create-pro-forma-button"
                    className="d-flex w-100 justify-content-center heap-deal-create-pro-forma-from-sensitivity"
                    callback={onCreateProForma}
                    color="primary"
                    text="Generate pro forma"
                    outline
                />
            </NSCard>
        </div>
    );
};

export default ProFormaExitValuationCard;
